// ConciergeChat - Main chat view component with place preview sheet

import React, { useEffect, useRef, useState } from 'react';
import { ArrowLeft, RotateCcw, Loader2 } from 'lucide-react';
import { useConcierge } from '../../hooks/useConcierge';
import { ConciergeBubble } from './ConciergeBubble';
import { ConciergeInput } from './ConciergeInput';
import { PlacePreviewSheet } from './PlacePreviewSheet';
import type { Place, Realm } from '../../types';

interface ConciergeChatProps {
  places: Place[];
  realm: Realm;
  userId?: string;
  userName?: string;
  onBack: () => void;
  onPlaceSelect: (placeId: string) => void;
  favorites?: string[];
  onToggleFavorite?: (placeId: string) => void;
}

export function ConciergeChat({
  places,
  realm,
  userId,
  userName,
  onBack,
  onPlaceSelect,
  favorites = [],
  onToggleFavorite,
}: ConciergeChatProps) {
  const {
    conversation,
    isLoading,
    isTyping,
    error,
    initializeChat,
    sendMessage,
    resetChat,
  } = useConcierge({ places, realm, userId, userName });

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Place preview sheet state
  const [previewPlace, setPreviewPlace] = useState<Place | null>(null);

  // Initialize on mount
  useEffect(() => {
    initializeChat();
  }, [initializeChat]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation?.messages]);

  const realmLabel = realm === 'goodwork' ? 'Arbeitsorte' : 'Freizeitorte';

  // Handle place preview
  const handlePlacePreview = (place: Place) => {
    setPreviewPlace(place);
  };

  // Handle view full details (navigate away)
  const handleViewFullDetails = () => {
    if (previewPlace) {
      onPlaceSelect(previewPlace.id);
    }
  };

  // Handle favorite toggle
  const handleToggleFavorite = () => {
    if (previewPlace && onToggleFavorite) {
      onToggleFavorite(previewPlace.id);
    }
  };

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-gray-200">
        <button
          onClick={onBack}
          className="p-2 -ml-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Zurueck"
        >
          <ArrowLeft className="w-5 h-5 text-gray-600" />
        </button>

        <div className="flex-1">
          <h2 className="font-semibold text-gray-900">GoodPlace Concierge</h2>
          <p className="text-xs text-gray-500">{realmLabel} finden</p>
        </div>

        <button
          onClick={resetChat}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          title="Neues Gespraech"
          aria-label="Neues Gespraech starten"
        >
          <RotateCcw className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {isLoading && !conversation ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
          </div>
        ) : (
          conversation?.messages.map((msg) => (
            <ConciergeBubble
              key={msg.id}
              message={msg}
              onPlacePreview={handlePlacePreview}
              places={places}
            />
          ))
        )}

        {/* Typing indicator */}
        {isTyping && (
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
            <span className="text-sm text-gray-500">Concierge denkt nach...</span>
          </div>
        )}

        {/* Error message */}
        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <ConciergeInput
        onSend={sendMessage}
        disabled={isLoading || isTyping}
        placeholder={
          realm === 'goodwork'
            ? 'Wo moechtest du arbeiten?'
            : 'Was fuer einen Ort suchst du?'
        }
      />

      {/* Place Preview Sheet */}
      {previewPlace && (
        <PlacePreviewSheet
          place={previewPlace}
          onClose={() => setPreviewPlace(null)}
          onViewFullDetails={handleViewFullDetails}
          onToggleFavorite={onToggleFavorite ? handleToggleFavorite : undefined}
          isFavorite={favorites.includes(previewPlace.id)}
        />
      )}
    </div>
  );
}

export default ConciergeChat;
